import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, SvgIcon } from '@mui/material'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CheckCircleSmallIcon from '@hapana/shared/web/styles/assets/svgs/check-circle-small.svg'
import EllipsisVIcon from '@hapana/shared/web/styles/assets/svgs/ellipsis-v.svg'
import ExclamationTriangleSmallIcon from '@hapana/shared/web/styles/assets/svgs/exclamation-triangle-small.svg'
import EyeSlashSmallIcon from '@hapana/shared/web/styles/assets/svgs/eye-slash-small.svg'
import EyeSmallIcon from '@hapana/shared/web/styles/assets/svgs/eye-small.svg'
import PenSmallIcon from '@hapana/shared/web/styles/assets/svgs/pen-small.svg'
import PlaySmallIcon from '@hapana/shared/web/styles/assets/svgs/play-small.svg'
import StopwatchSlashIcon from '@hapana/shared/web/styles/assets/svgs/stopwatch-slash-small.svg'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { StartTimerDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/waitline-tab/start-timer-dialog/start-timer-dialog.component'
import { EquipmentEditDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/equipment-edit-dialog/equipment-edit-dialog.component'
import { EquipmentActivityLogDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/equipment-activity-log-dialog/equipment-activity-log-dialog.component'
import { RequestMaintenanceDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/request-maintenance-dialog/request-maintenance-dialog.component'
import { StopTimerDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/stop-timer-dialog/stop-timer-dialog.component'
import { EQUIPMENT_STATUSES } from '_constants/equipments'
import { useResetEquipment, useUpdateEquipmentStatus } from '_hooks/equipments.hooks'
import usePermissionsStore from '_stores/permissions.store'

import { styles } from './equipment-actions-menu.styles'

export const EquipmentActionsMenu = ({ equipment, siteId, diagnostics }) => {
  const { t } = useTranslation(['checkIn'])
  const { pushDialog, closeDialog } = useDialog()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useUpdateEquipmentStatus()
  const { mutate: resetEquipment, isLoading: isResetting } = useResetEquipment()
  const { hasPermission } = usePermissionsStore()
  const canManage = hasPermission('wellness__equipments__manage')

  const handleOpen = (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const latest = equipment.assignments?.[0]
  const isLiveVersion = !!(diagnostics?.commPort && diagnostics.commPort !== 'TEST_BYPASS')
  const { status } = equipment

  const isDisabled = status === EQUIPMENT_STATUSES.DISABLED
  const canReset = status !== EQUIPMENT_STATUSES.AVAILABLE
  const canToggleAvailability = status === EQUIPMENT_STATUSES.AVAILABLE || isDisabled
  const canRequestMaintenance = isDisabled
  const canStopTimer =
    (status === EQUIPMENT_STATUSES.IN_USE || status === EQUIPMENT_STATUSES.DELAY) &&
    !!latest?.equipmentAssignmentId
  const canStartTest = status === EQUIPMENT_STATUSES.AVAILABLE

  return (
    <>
      <Button
        variant={BUTTON_VARIANTS.ICON}
        icon={EllipsisVIcon}
        size={BUTTON_SIZE.SMALL}
        label="row-actions"
        type="button"
        onClick={handleOpen}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 272 } }}
      >
        {canManage && (
          <MenuItem
            sx={styles.menuItem}
            onClick={() => {
              handleClose()
              pushDialog(EquipmentEditDialog, { equipmentId: equipment.equipmentId })
            }}
          >
            <ListItemIcon sx={styles.menuItemIcon}>
              <SvgIcon component={PenSmallIcon} />
            </ListItemIcon>
            <ListItemText sx={styles.menuItemText}>
              {t('checkIn:equipments.manageEquipments.edit')}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          sx={styles.menuItem}
          onClick={() => {
            handleClose()
            pushDialog(EquipmentActivityLogDialog, { equipmentId: equipment.equipmentId })
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <SvgIcon component={EyeSmallIcon} />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText}>
            {t('checkIn:equipments.manageEquipments.viewActivityLog')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          sx={styles.menuItem}
          disabled={isResetting || !canReset}
          onClick={() => {
            handleClose()
            resetEquipment({ equipmentId: equipment.equipmentId })
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <SvgIcon component={CheckCircleSmallIcon} />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText}>
            {t('checkIn:equipments.manageEquipments.reset')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          sx={styles.menuItem}
          disabled={isUpdatingStatus || !canToggleAvailability}
          onClick={() => {
            handleClose()
            updateStatus({
              status: isDisabled ? EQUIPMENT_STATUSES.AVAILABLE : EQUIPMENT_STATUSES.DISABLED,
              equipmentId: equipment.equipmentId,
              siteId,
            })
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <SvgIcon component={EyeSlashSmallIcon} />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText}>
            {isDisabled
              ? t('checkIn:equipments.manageEquipments.markAsAvailable')
              : t('checkIn:equipments.manageEquipments.markAsUnavailable')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          sx={styles.menuItem}
          disabled={!canRequestMaintenance}
          onClick={() => {
            handleClose()
            pushDialog(RequestMaintenanceDialog, {
              equipment: {
                equipmentId: equipment.equipmentId,
                siteId,
                name: equipment.name,
                image: equipment.image?.medium || equipment.image?.thumbnail || equipment.image?.db,
                manufacturer: equipment.manufacturer,
                timerId: equipment.timerId,
              },
            })
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <SvgIcon component={ExclamationTriangleSmallIcon} />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText}>
            {t('checkIn:equipments.manageEquipments.requestMaintenance')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          sx={styles.menuItem}
          disabled={!canStartTest}
          onClick={() => {
            handleClose()
            pushDialog(StartTimerDialog, {
              equipmentId: equipment.equipmentId,
              equipment,
              isTestMode: true,
              onSuccess: closeDialog,
            })
          }}
        >
          <ListItemIcon sx={styles.menuItemIcon}>
            <SvgIcon component={PlaySmallIcon} />
          </ListItemIcon>
          <ListItemText sx={styles.menuItemText}>
            {t('checkIn:equipments.detail.startTest')}
          </ListItemText>
        </MenuItem>
        <Divider sx={styles.menuDivider} />
        <MenuItem
          sx={styles.menuItem}
          disabled={!canStopTimer}
          onClick={() => {
            handleClose()
            pushDialog(StopTimerDialog, {
              assignmentId: latest?.equipmentAssignmentId,
              equipmentId: equipment.equipmentId,
              equipmentName: equipment.name,
              siteId,
              equipmentStatus: status,
              isLiveVersion,
            })
          }}
        >
          <ListItemIcon sx={canStopTimer ? styles.stopTimerIcon : styles.menuItemIcon}>
            <SvgIcon component={StopwatchSlashIcon} />
          </ListItemIcon>
          <ListItemText sx={canStopTimer ? styles.stopTimerText : styles.menuItemText}>
            {t('checkIn:equipments.manageEquipments.stopTimer')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

EquipmentActionsMenu.propTypes = {
  equipment: PropTypes.shape({
    equipmentId: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    siteId: PropTypes.string,
    manufacturer: PropTypes.string,
    timerId: PropTypes.number,
    image: PropTypes.shape({
      medium: PropTypes.string,
      thumbnail: PropTypes.string,
      db: PropTypes.string,
    }),
    assignments: PropTypes.arrayOf(
      PropTypes.shape({
        equipmentAssignmentId: PropTypes.string,
      })
    ),
  }).isRequired,
  siteId: PropTypes.string,
  diagnostics: PropTypes.shape({
    commPort: PropTypes.string,
  }),
}
